import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  ThumbsUp,
  MessageCircle,
  Repeat2,
  Send,
  MoreHorizontal,
  Globe2,
  Save,
  Loader2,
} from 'lucide-react';

/**
 * LinkedInPostEditor
 * ─────────────────────────────────────────────────────────────────
 * Zwei-Tab-Editor für LinkedIn-Posts — Aufbau analog zum BlogArticleViewer:
 *
 *   • Tab "Vorschau"  → LinkedIn-Feed-Look, direkt editierbar (contentEditable)
 *   • Tab "Text"      → reine Textarea (voller Post-Text)
 *
 * Der Zustand ist EINE Quelle der Wahrheit: `text` (plain text). Beide Tabs
 * lesen aus und schreiben in denselben State — daher automatische
 * bidirektionale Synchronisation, kein separater Draft, kein manuelles
 * "Übernehmen".
 *
 * Speicherung: über `onSave(nextText)` — der Parent persistiert via
 * PUT /api/content/{id}.
 */

const T = {
  de: {
    preview: 'Vorschau',
    text: 'Text',
    save: 'Speichern',
    saving: 'Speichert …',
    saved: 'Gespeichert',
    dirty: 'Ungespeicherte Änderungen',
    empty: 'Klicke in den Beitrag, um zu bearbeiten …',
    placeholder: 'Schreibe deinen LinkedIn-Beitrag …',
    seeMore: '… mehr anzeigen',
    seeLess: '… weniger anzeigen',
    ago: 'jetzt',
    reactions: '128',
    comments: '12 Kommentare',
    shares: '4× geteilt',
    like: 'Gefällt mir',
    comment: 'Kommentieren',
    share: 'Teilen',
    send: 'Senden',
    charsOf: 'Zeichen von',
    tooLong: 'zu viel!',
  },
  en: {
    preview: 'Preview',
    text: 'Text',
    save: 'Save',
    saving: 'Saving …',
    saved: 'Saved',
    dirty: 'Unsaved changes',
    empty: 'Click into the post to edit …',
    placeholder: 'Write your LinkedIn post …',
    seeMore: '… see more',
    seeLess: '… see less',
    ago: 'now',
    reactions: '128',
    comments: '12 comments',
    shares: '4 shares',
    like: 'Like',
    comment: 'Comment',
    share: 'Share',
    send: 'Send',
    charsOf: 'chars of',
    tooLong: 'too many!',
  },
};

const AUTHOR_ACCENT = '#0A66C2'; // LinkedIn brand blue

// Split plain text into an array of "runs": {type:'text'|'hashtag'|'mention', value}
function tokenize(line) {
  const parts = [];
  const regex = /(#[\w\u00C0-\u017F\d_]+|@[\w\u00C0-\u017F\d_.-]+)/g;
  let lastIdx = 0;
  let m;
  while ((m = regex.exec(line)) !== null) {
    if (m.index > lastIdx) parts.push({ type: 'text', value: line.slice(lastIdx, m.index) });
    parts.push({
      type: m[0].startsWith('#') ? 'hashtag' : 'mention',
      value: m[0],
    });
    lastIdx = regex.lastIndex;
  }
  if (lastIdx < line.length) parts.push({ type: 'text', value: line.slice(lastIdx) });
  return parts;
}

// Render tokens as inline React nodes with colored #tags / @mentions.
function renderInline(line, keyBase) {
  const tokens = tokenize(line);
  return tokens.map((tok, i) => {
    if (tok.type === 'hashtag' || tok.type === 'mention') {
      return (
        <span
          key={`${keyBase}-${i}`}
          style={{ color: AUTHOR_ACCENT }}
          className="font-medium"
        >
          {tok.value}
        </span>
      );
    }
    return <span key={`${keyBase}-${i}`}>{tok.value}</span>;
  });
}

/**
 * Convert plain text to HTML for the contentEditable region.
 * Uses <div> per line (browser default behaviour), preserves paragraph
 * breaks, colours hashtags / mentions.
 */
function textToHtml(text) {
  const lines = (text || '').split('\n');
  return lines
    .map((line) => {
      if (line === '') return '<div><br></div>';
      const escaped = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Highlight hashtags & mentions
      const withTokens = escaped.replace(
        /(#[\w\u00C0-\u017F\d_]+|@[\w\u00C0-\u017F\d_.-]+)/g,
        (m) => `<span style="color:${AUTHOR_ACCENT};font-weight:500">${m}</span>`
      );
      return `<div>${withTokens}</div>`;
    })
    .join('');
}

/**
 * Convert the contentEditable's innerText back to plain text.
 * We rely on browser innerText which already collapses <div>/<br>
 * to newlines correctly across Chrome / Safari / Firefox.
 */
function htmlToText(el) {
  if (!el) return '';
  // innerText respects visible line breaks; textContent flattens them
  return (el.innerText || '').replace(/\u00A0/g, ' ');
}

const LinkedInPostEditor = forwardRef(function LinkedInPostEditor(
  {
    text = '',
    hashtags = '',
    authorName = 'Dein Name',
    authorTagline = 'Deine Rolle · Unternehmen',
    avatarUrl = null,
    language = 'de',
    maxChars = 3000,
    onChange, // (nextText) — called on every keystroke in either view
    onSave, // async (nextText) — persist to backend
    contentKey, // bump this from parent when the source content_piece changed externally
    minHeight = 480, // shared height envelope for preview + text — prevents layout jumps on tab switch
  },
  ref
) {
  const t = T[language] || T.de;
  const [view, setView] = useState('preview');
  // Collapsed-first: mirror the real LinkedIn feed where posts start
  // truncated with "…mehr anzeigen". The user can expand explicitly, but
  // the collapsed state is restored after Save so the editor keeps
  // matching what a viewer will actually see.
  const [expanded, setExpanded] = useState(false);
  // ── Canonical text = text + hashtags merged ──────────────────────────
  // The stored `text_content` is treated as the single source of truth.
  // If the DB still has hashtags in a separate field (legacy), we merge
  // them into `current` ONCE on mount / contentKey change. From then on
  // preview, text tab and save-payload all use the same string.
  const mergeHashtags = (t, h) => {
    const txt = (t || '').trimEnd();
    const hs = (h || '').trim();
    if (!hs) return txt;
    const firstTag = hs.split(/\s+/).find(Boolean);
    if (firstTag && txt.includes(firstTag)) return txt;
    return txt ? `${txt}\n\n${hs}` : hs;
  };

  const [current, setCurrent] = useState(() => mergeHashtags(text, hashtags));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const editableRef = useRef(null);
  const lastContentKey = useRef(contentKey);
  const lastSyncedText = useRef(mergeHashtags(text, hashtags));
  // Letzter Text, den DIESER Editor selbst nach oben gemeldet hat. Weicht die `text`-Prop davon ab,
  // kam der Text von außen (z. B. KI-Überarbeitung, Version laden) und muss angezeigt werden —
  // unabhängig davon, ob der Parent den contentKey hochzählt.
  const lastEmittedText = useRef(text);

  // External re-sync: when the parent bumps `contentKey` (i.e. the
  // underlying content piece was replaced — e.g. a regenerate call
  // returned new text), pull the new text in and reset dirty flag.
  useEffect(() => {
    const keyChanged = contentKey !== lastContentKey.current;
    const externalText = text !== lastEmittedText.current;
    if (keyChanged || externalText) {
      lastContentKey.current = contentKey;
      lastEmittedText.current = text;
      const merged = mergeHashtags(text, hashtags);
      setCurrent(merged);
      lastSyncedText.current = merged;
      setDirty(false);
      setExpanded(false);
      if (editableRef.current) {
        editableRef.current.innerHTML = textToHtml(merged);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey, text, hashtags]);

  // On mount / view switch to preview: paint initial HTML into contentEditable.
  useEffect(() => {
    if (view !== 'preview') return;
    if (!editableRef.current) return;
    // Only re-paint if the DOM's current text doesn't match `current`.
    // This prevents caret jumps while the user types (we never overwrite
    // during active editing, only on tab-switch or external re-sync).
    const domText = htmlToText(editableRef.current);
    if (domText !== current) {
      editableRef.current.innerHTML = textToHtml(current);
    }
  }, [view, current]);

  // Expose imperative API for the parent (e.g. force-save-on-close).
  useImperativeHandle(ref, () => ({
    getText: () => current,
    isDirty: () => dirty,
    save: async () => {
      await doSave();
    },
  }));

  const setFromView = (next) => {
    lastEmittedText.current = next;
    setCurrent(next);
    setDirty(next !== lastSyncedText.current);
    if (onChange) onChange(next);
  };

  const onPreviewInput = (e) => {
    const next = htmlToText(e.currentTarget);
    setFromView(next);
  };

  // Ensure plain-text paste (no styles / images / <b>).
  const onPreviewPaste = (e) => {
    e.preventDefault();
    const t2 = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
    document.execCommand('insertText', false, t2);
  };

  const onTextChange = (e) => setFromView(e.target.value);

  const doSave = async () => {
    if (!onSave || saving) return;
    setSaving(true);
    try {
      await onSave(current);
      lastSyncedText.current = current;
      setDirty(false);
      setSavedAt(Date.now());
      // Reset preview back to collapsed feed state so the user immediately
      // sees the "…mehr anzeigen" hook again — matches real LinkedIn.
      setExpanded(false);
      setTimeout(() => setSavedAt(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  // Character counter (of the raw post text, without the merged-in hashtags
  // block — since hashtags are stored in a separate field).
  const charCount = (current || '').length;
  const isOverLimit = maxChars && charCount > maxChars;
  const isNearLimit = maxChars && charCount > maxChars * 0.9;

  // For the preview render (non-editable pieces around the editable body).
  const authorInitials = (authorName || 'X')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="space-y-3" data-testid="linkedin-post-editor">
      <style>{`
        .li-editor-body {
          outline: none;
          white-space: pre-wrap;
          word-break: break-word;
          min-height: 3.6em;
          caret-color: #0A66C2;
        }
        .li-editor-body > div {
          min-height: 1.4em;
        }
        .li-editor-body:empty::before {
          content: attr(data-placeholder);
          color: #00000066;
          pointer-events: none;
        }
        /* Collapsed feed state — mirrors LinkedIn's "3 visible lines" cutoff.
           We use max-height (not line-clamp) because contentEditable wraps
           every line in its own <div>, which breaks -webkit-line-clamp. */
        .li-editor-body.collapsed {
          max-height: 4.5em;
          overflow: hidden;
          mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
          -webkit-mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
        }
      `}</style>

      {/* ── Toolbar (tabs + save) — visually mirrors BlogArticleViewer ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div
          className="inline-flex rounded-lg bg-[#0A1628] p-1"
          data-testid="linkedin-editor-tabs"
        >
          <button
            onClick={() => setView('preview')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === 'preview'
                ? 'bg-[#0A66C2] text-white'
                : 'text-gray-300 hover:text-white'
            }`}
            data-testid="linkedin-editor-tab-preview"
          >
            {t.preview}
          </button>
          <button
            onClick={() => setView('text')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === 'text'
                ? 'bg-[#0A66C2] text-white'
                : 'text-gray-300 hover:text-white'
            }`}
            data-testid="linkedin-editor-tab-text"
          >
            {t.text}
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {dirty && !saving && (
            <span className="text-yellow-400" data-testid="linkedin-editor-dirty">
              ● {t.dirty}
            </span>
          )}
          {onSave && (
            <button
              onClick={doSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-[#0A66C2] text-white hover:bg-[#084d94] transition-colors disabled:opacity-60"
              data-testid="linkedin-editor-save"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              {saving ? t.saving : savedAt ? t.saved : t.save}
            </button>
          )}
        </div>
      </div>

      {/* ── View container — no forced min-height on preview to avoid dead
           space between the LinkedIn card and the character counter. The
           text tab enforces its own min-height via the textarea (below), so
           the toolbar / save button (above this container) stay put and
           only the char counter sits directly under the actual content. ── */}
      <div className="relative" data-testid="linkedin-editor-height-envelope">

      {/* ── PREVIEW VIEW — LinkedIn feed card, editable body ── */}
      {view === 'preview' && (
        <div
          className="mx-auto max-w-[560px] rounded-lg border border-[#38434F] bg-white text-[#000000E6] shadow-sm font-sans"
          data-testid="linkedin-post-preview"
        >
          {/* Header */}
          <div className="flex items-start justify-between p-3">
            <div className="flex items-start gap-2">
              <div
                className="h-12 w-12 flex-shrink-0 rounded-full bg-[#0A66C2] flex items-center justify-center text-white font-semibold text-sm overflow-hidden"
                data-testid="linkedin-preview-avatar"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={authorName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  authorInitials
                )}
              </div>
              <div className="min-w-0">
                <div
                  className="font-semibold text-sm text-[#000000E6] leading-tight"
                  data-testid="linkedin-preview-author"
                >
                  {authorName}
                </div>
                <div className="text-xs text-[#00000099] leading-tight mt-0.5 line-clamp-1">
                  {authorTagline}
                </div>
                <div className="flex items-center gap-1 text-xs text-[#00000099] mt-1">
                  <span>{t.ago}</span>
                  <span>·</span>
                  <Globe2 className="h-3 w-3" />
                </div>
              </div>
            </div>
            <button
              className="p-1 text-[#00000099] hover:bg-[#F3F2EF] rounded"
              aria-label="Mehr"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </div>

          {/* Editable body */}
          <div className="px-3 pb-3">
            <div
              ref={editableRef}
              contentEditable
              suppressContentEditableWarning
              onInput={onPreviewInput}
              onPaste={onPreviewPaste}
              onFocus={() => {
                // When the user clicks into the body to edit, expand
                // automatically so the caret is not fighting the clamp.
                if (!expanded) setExpanded(true);
              }}
              spellCheck
              className={`li-editor-body text-[14px] leading-[1.43] focus:outline-none rounded${
                expanded ? '' : ' collapsed'
              }`}
              data-testid="linkedin-preview-body"
              data-placeholder={t.placeholder}
            />
            {!expanded && (current || '').length > 0 && (
              <button
                onClick={() => setExpanded(true)}
                className="mt-1 text-[13px] text-[#00000099] hover:text-[#000000E6] font-normal"
                data-testid="linkedin-preview-see-more"
                type="button"
              >
                {t.seeMore}
              </button>
            )}
            {expanded && (current || '').length > 0 && (
              <button
                onClick={() => {
                  setExpanded(false);
                  // Move focus off the contentEditable so the onFocus handler
                  // does not immediately re-expand.
                  if (editableRef.current) editableRef.current.blur();
                }}
                className="mt-1 text-[13px] text-[#00000099] hover:text-[#000000E6] font-normal"
                data-testid="linkedin-preview-see-less"
                type="button"
              >
                {t.seeLess}
              </button>
            )}
            {/* Hashtags are now part of `current` (single source of truth).
                The old separate "appended" hint has been removed — Preview
                and Text tab both show the same merged content. */}
          </div>

          {/* Reactions bar (visual only) */}
          <div className="flex items-center justify-between px-3 py-1.5 text-xs text-[#00000099] border-b border-[#00000014]">
            <div className="flex items-center gap-1">
              <div className="flex -space-x-1">
                <span className="h-4 w-4 rounded-full bg-[#0A66C2] flex items-center justify-center text-white text-[10px]">
                  👍
                </span>
                <span className="h-4 w-4 rounded-full bg-[#DF704D] flex items-center justify-center text-white text-[10px]">
                  🎉
                </span>
              </div>
              <span className="ml-1">{t.reactions}</span>
            </div>
            <div>
              {t.comments} · {t.shares}
            </div>
          </div>

          {/* Action bar */}
          <div className="grid grid-cols-4 text-xs text-[#00000099] font-semibold">
            {[
              { icon: ThumbsUp, label: t.like },
              { icon: MessageCircle, label: t.comment },
              { icon: Repeat2, label: t.share },
              { icon: Send, label: t.send },
            ].map(({ icon: Icon, label }) => (
              <button
                key={label}
                className="flex items-center justify-center gap-1.5 py-2 hover:bg-[#F3F2EF] transition-colors"
                tabIndex={-1}
                type="button"
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── TEXT VIEW — raw plaintext editor ── */}
      {view === 'text' && (
        <div
          className="rounded-lg border border-transparent focus-within:border-[#00D9C8]/40 bg-[#1A2B42] transition-colors h-full"
          data-testid="linkedin-editor-text-wrapper"
        >
          <textarea
            value={current}
            onChange={onTextChange}
            placeholder={t.placeholder}
            style={{ minHeight: `${Math.max(300, minHeight - 40)}px` }}
            className="w-full bg-transparent text-white text-sm leading-relaxed p-3 resize-y focus:outline-none whitespace-pre-wrap"
            data-testid="linkedin-editor-text"
          />
        </div>
      )}

      </div>
      {/* ── /Shared height envelope ── */}

      {/* ── Char counter (identical position/style as BlogEditor) ── */}
      {maxChars > 0 && (
        <div
          className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
            isOverLimit
              ? 'bg-red-500/20 text-red-400'
              : isNearLimit
              ? 'bg-yellow-500/20 text-yellow-400'
              : 'bg-[#0A1628] text-[#5A6C7D]'
          }`}
        >
          <span data-testid="linkedin-editor-char-count">
            {charCount.toLocaleString()} / {maxChars.toLocaleString()} {t.charsOf.split(' ')[0]}
          </span>
          {isOverLimit && (
            <span className="font-medium">
              {(charCount - maxChars).toLocaleString()} {t.tooLong}
            </span>
          )}
        </div>
      )}

      {/* Preserve the previously-existing helper renderer for external
          use (renderInline is currently unused externally but exported
          for possible re-use). */}
      <span className="sr-only">{renderInline('', 'unused')}</span>
    </div>
  );
});

export default LinkedInPostEditor;
